import * as React from "react";
import Widget from "./containers/Widget";
import * as components from "./containers";
import { ComponentConfig, WidgetConfig } from "./components/Widget/Widget";
import { StoreState } from "./reducers";
import { connect } from "react-redux";
import uniq from "lodash/uniq";
import startCase from "lodash/startCase";
import isEqual from "lodash/isEqual";
import get from "lodash/get";
import compact from "lodash/compact";
import cloneDeep from "lodash/cloneDeep";

import {
  getAppleWalletResponse,
  getCampaignId,
  getDisplaySize,
  getGoogleWalletResponse,
  getIsOverlay,
  getMerchantId,
  getReferralResponse,
  getRewardStats,
  getScreenName,
  getShareResponse,
  getWidgetConfig,
  getWidgetData,
  getWidgetId,
} from "./reducers/methods";

import {
  sendIsReady,
  setCampaignId,
  setDisplaySize,
  setMerchantId,
  setOverlay,
  setScreenName,
  setWidgetConfigId,
  setWidgetId,
  widgetEventTrack,
} from "./actions";
import { getQueryVariable } from "./utils/url";
import { getContentBetweenTwoStrings } from "./utils/getContentBetweenTwoStrings";
import { CheckboxGlyphStyles, InputPlaceholderStyles } from "./components/util";
import {
  customerAuthenticationOccurred,
  customerEmailChanged,
  customerIsAuthenticated,
  customerLogOutOccurred,
} from "./utils/customer";
import { IndexScreen, RouterRule } from "./types";
import { getScreen } from "./utils/getScreen";

interface AppProps {
  merchantId: string | null;
  campaignId: string | null;
  widgetId: string | null;
  appleWallet: StoreState["referral"]["appleWallet"];
  referral: StoreState["referral"]["referral"];
  googleWallet: StoreState["referral"]["googleWallet"];
  rewardStats: StoreState["reward"]["rewardStats"];
  share: StoreState["share"]["share"];
  // tslint:disable-next-line:no-any
  config: any;
  displaySize: string;
  isOverlay: boolean;
  screenName: string;
  setMerchantId: typeof setMerchantId;
  setDisplaySize: typeof setDisplaySize;
  setOverlay: typeof setOverlay;
  setScreenName: typeof setScreenName;
  setWidgetConfigId: typeof setWidgetConfigId;
  setWidgetId: typeof setWidgetId;
  setCampaignId: typeof setCampaignId;
  sendIsReady: typeof sendIsReady;
  widgetEventTrack: typeof widgetEventTrack;
  widgetData: any;
}

class App extends React.PureComponent<AppProps, { windowWidth: number }> {
  constructor(props: AppProps) {
    super(props);

    this.state = { windowWidth: 0 };
  }

  componentDidMount() {
    const scale = getQueryVariable("scale");
    const campaignId = getQueryVariable("campaignId");
    const merchantId = getQueryVariable("merchantId");
    const widgetId = getQueryVariable("widgetId");
    const widgetConfigId = getQueryVariable("widgetConfigId");
    const overlay = getQueryVariable("overlay");

    if (scale) {
      document.body.style.transform = `scale(${scale})`;
      document.body.style.width = `${100 / parseFloat(scale)}%`;
      document.body.style.height = `${100 / parseFloat(scale)}%`;
      document.body.style.transformOrigin = "top left";
    }

    window.addEventListener("resize", this.handleScreenResize);

    // merchantId, widgetId and CampaignId should all be defined
    if (merchantId && campaignId && widgetId && widgetConfigId) {
      this.props.setWidgetConfigId(widgetConfigId);
      this.props.setWidgetId(widgetId);
      this.props.setCampaignId(campaignId);
      this.props.setMerchantId(merchantId);
      this.props.setOverlay(overlay);
      this.updateDisplaySizeState();
      this.props.sendIsReady();
    }
  }

  componentDidUpdate(prevProps: AppProps) {
    if (!isEqual(prevProps.config, this.props.config)) {
      this.insertFonts();
    }

    // update display size state if window width is 0
    // handles a bug where iframeResizer can initially set an iframe
    // width of 0
    // https://github.com/davidjbradshaw/iframe-resizer/issues/629
    if (this.state.windowWidth === 0 && window.innerWidth > 0) {
      this.updateDisplaySizeState();
    }

    const reeavluateScreenName = () => {
      const { config } = this.props;

      if (!config.index) {
        // initial redux store config data. ignore
        // evaluation of screen name since config data
        // is not actually available yet.
        return;
      }

      const indexScreen = config.index as IndexScreen;
      const indexIsRouter =
        indexScreen.component.toLowerCase() === "router" &&
        indexScreen.rules &&
        indexScreen.rules.length > 0;

      if (indexIsRouter) {
        const rules = indexScreen.rules as RouterRule[];
        const screenName = getScreen(rules, this.props.widgetData);

        this.props.setScreenName(screenName as string);
      }
    };

    // Router component is mounted momentarily to determine screen to load
    // based on data available on mount and is subsequently unmounted. The
    // following checks are necessary to re-evaluate the screen being
    // displayed as data changes during session.
    if (customerLogOutOccurred(prevProps.widgetData, this.props.widgetData)) {
      reeavluateScreenName();
    }

    if (
      customerAuthenticationOccurred(
        prevProps.widgetData,
        this.props.widgetData
      )
    ) {
      reeavluateScreenName();
    }

    if (
      customerEmailChanged(prevProps.widgetData, this.props.widgetData) &&
      customerIsAuthenticated(this.props.widgetData)
    ) {
      reeavluateScreenName();
    }
  }

  updateDisplaySizeState = () => {
    const windowWidth = window.innerWidth;

    const displaySize = windowWidth < 600 ? "mobile" : "desktop";
    this.props.setDisplaySize(displaySize);
    this.setState(() => ({ windowWidth }));
  };

  handleScreenResize = () => {
    this.updateDisplaySizeState();
  };

  augmentConfigForMobile = () => {
    if (this.props.displaySize === "mobile") {
      // From merchant sdk
      const deviceConfig = cloneDeep(this.props.config);
      for (const componentId in deviceConfig.mobileStyle) {
        if (
          deviceConfig.mobileStyle.hasOwnProperty(componentId) &&
          componentId in deviceConfig
        ) {
          const componentStyle = (deviceConfig[componentId] || ({} as any))
            .style;
          const mobileStyles = (deviceConfig.mobileStyle || {})[
            componentId
          ] as {};

          const style = {
            ...componentStyle,
            ...mobileStyles,
          };

          deviceConfig[componentId] = {
            ...(deviceConfig[componentId] as {}),
            style,
          };
        }
      }

      return deviceConfig;
    } else {
      return this.props.config;
    }
  };

  insertFonts = () => {
    const { config } = this.props;
    const fonts = compact(
      uniq(
        Object.values(config).map((component: ComponentConfig) =>
          get(component, "font", "")
        )
      )
    );
    const errorFont = get(config, "settings.customErrorStyle.font");

    if (errorFont) {
      fonts.push(errorFont);
    }

    const successFont = get(config, "settings.customSuccessStyle.font");

    if (successFont) {
      fonts.push(successFont);
    }

    fonts.forEach((font) => {
      const fontStyle = document.createElement("style");
      const fontFamily = startCase(
        getContentBetweenTwoStrings(font, "/", ".", true)
      );
      const fontFace = document.createTextNode(`\
      @font-face {
          font-family: '${fontFamily}';
          src: local(${fontFamily}), url('${font}');
      }
      `);

      fontStyle.appendChild(fontFace);
      document.head.appendChild(fontStyle);
    });
  };

  render() {
    const {
      appleWallet,
      config,
      googleWallet,
      referral,
      rewardStats,
      share,
      widgetData,
      screenName,
      widgetEventTrack,
    } = this.props;

    if (!config) {
      return null;
    }

    const deviceConfig = this.augmentConfigForMobile();

    const componentConfig: ComponentConfig = deviceConfig[
      screenName
    ] as ComponentConfig;

    if (!componentConfig) {
      return null;
    }

    const { inputPlaceholderColor, checkboxBorderColor } = deviceConfig;

    return (
      <>
        {inputPlaceholderColor && (
          <InputPlaceholderStyles color={inputPlaceholderColor} />
        )}
        {checkboxBorderColor && (
          <CheckboxGlyphStyles borderColor={checkboxBorderColor} />
        )}
        <Widget
          {...componentConfig}
          component={componentConfig.component}
          components={components}
          config={deviceConfig as WidgetConfig}
          data={widgetData}
          key={screenName}
          appleWallet={appleWallet}
          configName={screenName}
          googleWallet={googleWallet}
          referral={referral}
          rewardStats={rewardStats}
          share={share}
          isEditing={get(deviceConfig, "__editMode__", false)}
          widgetEventTrack={widgetEventTrack}
        />
      </>
    );
  }
}

const mapStateToProps = (state: StoreState) => {
  const config = getWidgetConfig(state) as WidgetConfig;
  const isEditMode = get(config, "__editMode__", false);
  const previewScreen = get(config, "__previewScreen__") as string | undefined;
  // In edit mode the builder can force which screen is shown (e.g. Login vs Dashboard).
  const screenName =
    isEditMode && previewScreen ? previewScreen : getScreenName(state);

  return {
    appleWallet: getAppleWalletResponse(state),
    campaignId: getCampaignId(state),
    config,
    displaySize: getDisplaySize(state),
    googleWallet: getGoogleWalletResponse(state),
    isOverlay: getIsOverlay(state),
    merchantId: getMerchantId(state),
    referral: getReferralResponse(state),
    rewardStats: getRewardStats(state),
    screenName,
    share: getShareResponse(state),
    widgetId: getWidgetId(state),
    widgetData: getWidgetData(state),
  };
};

export default connect(mapStateToProps, {
  sendIsReady,
  setCampaignId,
  setDisplaySize,
  setMerchantId,
  setOverlay,
  setScreenName,
  setWidgetConfigId,
  setWidgetId,
  widgetEventTrack,
})(App);
